import { useEffect, useRef, useState } from 'react';
import type { CatalogSection } from '../../api/catalogApi';
import { useTheme } from '../../context/ThemeContext';
import { ICONS } from './art';
import Raw from './Raw';

interface HubSidebarProps {
  sections: CatalogSection[];
  openSections: Record<string, boolean>;
  activeSectionId: string | null;
  activeToolId: string | null;
  username: string;
  role: string;
  showAccess: boolean;
  accessActive: boolean;
  onOpenAccess: () => void;
  onToggleSection: (sectionId: string) => void;
  onSelectTool: (sectionId: string, toolId: string) => void;
  onLogout: () => void;
}

const CHEVRON = (
  <svg className="chev" viewBox="0 0 24 24">
    <path d="m9 6 6 6-6 6" />
  </svg>
);

export default function HubSidebar({
  sections,
  openSections,
  activeSectionId,
  activeToolId,
  username,
  role,
  showAccess,
  accessActive,
  onOpenAccess,
  onToggleSection,
  onSelectTool,
  onLogout,
}: HubSidebarProps) {
  const { theme, toggleTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onPointer = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  return (
    <aside className="rail" aria-label="Sidebar">
      <div className="rail-head">
        <div className="ws" title="Avengers">
          <div className="brand-mark">A</div>
          <div className="two">
            <b>Avengers</b>
            <span>Internal automation</span>
          </div>
        </div>
      </div>
      <div className="rail-body">
        <div className="grp">
          <div className="rail-label">Workspace</div>
          <ul className="nav">
            {sections.map((section) => {
              const isOpen = !!openSections[section.id];
              const isActive = activeSectionId === section.id;
              return (
                <li key={section.id}>
                  <button
                    className="mb"
                    title={section.name}
                    aria-expanded={isOpen}
                    aria-current={isActive && !activeToolId}
                    onClick={() => onToggleSection(section.id)}
                  >
                    <Raw
                      html={(ICONS[section.icon] ?? '').replace('<svg', '<svg class="ic"')}
                      style={{ display: 'contents' }}
                    />
                    <span className="name">{section.name}</span>
                    <span className="count">{section.items.length}</span>
                    {CHEVRON}
                  </button>
                  {isOpen && (
                    <ul className="sub">
                      {section.items.map((tool) => (
                        <li key={tool.id}>
                          <button
                            className="sb"
                            aria-current={isActive && activeToolId === tool.id}
                            onClick={() => onSelectTool(section.id, tool.id)}
                          >
                            {tool.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
        {showAccess && (
          <div className="grp">
            <div className="rail-label">Administration</div>
            <ul className="nav">
              <li>
                <button className="mb" aria-current={accessActive} onClick={onOpenAccess}>
                  <svg className="ic" viewBox="0 0 24 24">
                    <path d="M12 3 5 6v5c0 4.5 3 8 7 10 4-2 7-5.5 7-10V6l-7-3Z" />
                    <path d="m9 12 2 2 4-4" />
                  </svg>
                  <span className="name">Access Management</span>
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
      <div className="rail-foot" ref={menuRef}>
        {menuOpen && (
          <div className="um-menu" role="menu" aria-label="Account menu">
            <div className="um-head">
              <div className="avatar">{username.charAt(0).toUpperCase()}</div>
              <div className="um-id">
                <b>{username}</b>
                <span>{role || 'No role assigned'}</span>
              </div>
            </div>
            <div className="um-sep" />
            <button className="um-item" role="menuitem" onClick={toggleTheme}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                {theme === 'dark' ? (
                  <>
                    <circle cx="12" cy="12" r="4" />
                    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
                  </>
                ) : (
                  <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
                )}
              </svg>
              <span>Theme</span>
              <em>{theme === 'dark' ? 'Dark' : 'Light'}</em>
            </button>
            <div className="um-sep" />
            <button
              className="um-item"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                onLogout();
              }}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
              </svg>
              <span>Log out</span>
            </button>
          </div>
        )}
        <button
          className="user"
          title="Account menu"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
        >
          <div className="avatar">{username.charAt(0).toUpperCase()}</div>
          <div className="two">
            <b>{username}</b>
            <span>{role || 'Account'}</span>
          </div>
          <svg className="um-chev" viewBox="0 0 24 24" aria-hidden="true">
            <path d="m8 10 4-4 4 4M8 14l4 4 4-4" />
          </svg>
        </button>
      </div>
    </aside>
  );
}
